/**
 * Shared Keycloak session-lifecycle hook.
 *
 * Encapsulates the non-JSX timing and activity logic that governs the BFF session:
 *  - token refresh on genuine user activity and on tab-return
 *  - idle-warning state (idle clock, countdown)
 *  - absolute 12-hour cap warning and sign-out
 *  - server-side activity recording (throttled POST /auth/activity)
 *  - session termination via POST /auth/logout when signedOutReason is set
 *
 * This hook has NO JSX — all rendering decisions belong to the consuming component,
 * which keeps app-specific UI (dialog components, toast libraries) out of the package.
 *
 * Usage:
 *   const { idleWarnOpen, idleRemaining, handleStay } = useKeycloakSessionGuard({
 *     signedOutReason,
 *     onSessionEnded,
 *     onCapWarning: (minutes) => toast.warning(`Session ends in ${minutes} min`),
 *   })
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { AuthUrlContext } from './authUrlContext'
import { useAuthInfo } from './useAuthInfo'

/** Why the session ended — drives copy in the consuming component's signed-out dialog. */
export type SignedOutReason = 'inactivity' | 'session_limit' | 'expired'

// Idle (AC-11): Keycloak's effective idle timeout is ~15 min. Warn at 12, sign out at 14 —
// a minute before Keycloak would, so the app owns the logout UX.
const IDLE_WARN_AFTER_MS = 12 * 60 * 1000
const IDLE_LOGOUT_AFTER_MS = 14 * 60 * 1000
// Absolute cap (AC-12): 12h from login, enforced server-side by sso_session_max_lifespan in
// keycloak-realm main.tf — this constant MUST match it. Heads-up 5 min before; sign out a
// minute before Keycloak would 401.
const MAX_SESSION_MS = 12 * 60 * 60 * 1000
const CAP_WARN_BEFORE_MS = 5 * 60 * 1000
const CAP_LOGOUT_BEFORE_MS = 60 * 1000
// Token refresh — activity-driven only (an idle session is left for Keycloak to time out).
const REFRESH_LEAD_MS = 30 * 1000
const REFRESH_THROTTLE_MS = 30 * 1000
const WATCHDOG_TICK_MS = 15 * 1000
const ACTIVITY_THROTTLE_MS = 1000
// Server-side activity-report throttle: at most one POST /auth/activity per minute while
// active, so scrolling doesn't flood it. The BFF's idle clock resets on this call, never
// on a background poll.
const ACTIVITY_RECORD_THROTTLE_MS = 60 * 1000

// Deliberately NOT 'mousemove': bare cursor drift (or a mouse-jiggler) must not keep a
// session alive — that would defeat AC-11's unattended-session timeout. Only meaningful
// interaction counts; real reading scrolls/clicks, which are covered.
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'keydown',
  'pointerdown',
  'scroll',
  'touchstart',
  'wheel',
]

export interface UseKeycloakSessionGuardOptions {
  /** Current signed-out reason — null while the session is active.
   *  When non-null, the hook terminates the Keycloak session and stops all timers. */
  signedOutReason: SignedOutReason | null
  /** Called when the hook decides the session should end. The caller keeps the first reason. */
  onSessionEnded: (reason: SignedOutReason) => void
  /** Optional: called once when the session is ~5 min from the 12h absolute cap.
   *  Use to show a toast or notification. The minutes argument is the rounded time left. */
  onCapWarning?: (minutesLeft: number) => void
}

export interface UseKeycloakSessionGuardResult {
  /** Whether the idle-warning prompt should be shown. */
  idleWarnOpen: boolean
  /** Remaining seconds before idle sign-out (meaningful only when idleWarnOpen is true). */
  idleRemaining: number
  /** Call when the user confirms "Stay signed in". Resets the idle clock and refreshes the token. */
  handleStay: () => void
}

export function useKeycloakSessionGuard({
  signedOutReason,
  onSessionEnded,
  onCapWarning,
}: UseKeycloakSessionGuardOptions): UseKeycloakSessionGuardResult {
  const authUrl = useContext(AuthUrlContext)
  const auth = useAuthInfo()
  const { isLoggedIn, accessTokenExpiresAtSeconds, refreshAuthInfo } = auth
  const sessionStartedAtSeconds = auth.isLoggedIn
    ? auth.sessionStartedAtSeconds
    : undefined

  const [idleWarnOpen, setIdleWarnOpen] = useState<boolean>(false)
  const [idleRemaining, setIdleRemaining] = useState<number>(0)

  // Latest values in refs so long-lived listeners and intervals read current values
  // without re-subscribing on every render.
  const lastActivityRef = useRef(Date.now())
  const lastRefreshRef = useRef(0)
  const lastActivityRecordedRef = useRef(0)
  const expiresAtRef = useRef(accessTokenExpiresAtSeconds)
  const refreshRef = useRef(refreshAuthInfo)
  const idleWarnOpenRef = useRef(idleWarnOpen)
  const signedOutRef = useRef(signedOutReason)
  const onSessionEndedRef = useRef(onSessionEnded)
  const onCapWarningRef = useRef(onCapWarning)
  const capWarnedRef = useRef(false)
  const capAtRef = useRef<number | null>(null)
  expiresAtRef.current = accessTokenExpiresAtSeconds
  refreshRef.current = refreshAuthInfo
  idleWarnOpenRef.current = idleWarnOpen
  signedOutRef.current = signedOutReason
  onSessionEndedRef.current = onSessionEnded
  onCapWarningRef.current = onCapWarning
  // Absolute cap epoch (ms), or null when auth_time is unknown → skip the cap UX (the server
  // still enforces the real cap; a hit arrives as a store-reported 'expired' sign-out).
  capAtRef.current = sessionStartedAtSeconds
    ? sessionStartedAtSeconds * 1000 + MAX_SESSION_MS
    : null

  const runRefresh = useCallback((): void => {
    lastRefreshRef.current = Date.now()
    void refreshRef.current()
  }, [])

  const recordActivity = useCallback(
    (now: number): void => {
      // Tell the BFF a human is present so its server-side idle clock resets. Throttled
      // hard — scrolling must not spam it; background polls can never make this call.
      if (now - lastActivityRecordedRef.current < ACTIVITY_RECORD_THROTTLE_MS)
        return
      lastActivityRecordedRef.current = now
      void fetch(`${authUrl}/auth/activity`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {})
    },
    [authUrl],
  )

  const endSession = useCallback((reason: SignedOutReason): void => {
    setIdleWarnOpen(false)
    onSessionEndedRef.current(reason)
  }, [])

  // Terminate the Keycloak session whenever signedOutReason becomes non-null (AC-11/AC-12).
  // Idempotent: a store-reported 'expired' end is already dead server-side, so the POST is
  // a harmless no-op there.
  useEffect(() => {
    if (!signedOutReason) return
    void fetch(`${authUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {})
  }, [signedOutReason, authUrl])

  // Activity → reset the idle clock + refresh a near-expiry token. Ignored while the idle
  // warning is open (dismissing it requires a deliberate "Stay signed in" click) or after
  // sign-out.
  useEffect(() => {
    if (!isLoggedIn) return
    let lastHandled = 0
    const onActivity = (): void => {
      if (idleWarnOpenRef.current || signedOutRef.current) return
      const now = Date.now()
      if (now - lastHandled < ACTIVITY_THROTTLE_MS) return
      lastHandled = now
      lastActivityRef.current = now
      recordActivity(now)
      const expiresAt = expiresAtRef.current
      if (
        expiresAt !== undefined &&
        expiresAt * 1000 - now < REFRESH_LEAD_MS &&
        now - lastRefreshRef.current > REFRESH_THROTTLE_MS
      ) {
        runRefresh()
      }
    }
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true })
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity)
      }
    }
  }, [isLoggedIn, runRefresh, recordActivity])

  // On returning to the tab (focus / visibility), catch up on what the interval watchdog
  // may have missed — browsers throttle timers in backgrounded tabs. If the idle clock is
  // already past the limit, sign out; otherwise refresh a stale access token so the tab's
  // refetch-on-focus doesn't 401. Focus is deliberately NOT treated as user activity (AC-11):
  // the idle clock is left untouched, so a glanced-at-but-untouched tab still times out.
  useEffect(() => {
    if (!isLoggedIn) return
    const onReveal = (): void => {
      if (document.visibilityState === 'hidden') return
      if (idleWarnOpenRef.current || signedOutRef.current) return
      const now = Date.now()
      if (now - lastActivityRef.current >= IDLE_LOGOUT_AFTER_MS) {
        endSession('inactivity')
        return
      }
      const expiresAt = expiresAtRef.current
      if (
        expiresAt !== undefined &&
        expiresAt * 1000 - now < REFRESH_LEAD_MS &&
        now - lastRefreshRef.current > REFRESH_THROTTLE_MS
      ) {
        runRefresh()
      }
    }
    window.addEventListener('focus', onReveal)
    document.addEventListener('visibilitychange', onReveal)
    return () => {
      window.removeEventListener('focus', onReveal)
      document.removeEventListener('visibilitychange', onReveal)
    }
  }, [isLoggedIn, endSession, runRefresh])

  // Watchdog: the absolute 12h cap (heads-up then hard sign-out) and the idle
  // warning/sign-out.
  useEffect(() => {
    if (!isLoggedIn) return
    const id = window.setInterval(() => {
      if (signedOutRef.current) return
      const now = Date.now()
      const capAt = capAtRef.current
      if (capAt !== null) {
        if (now >= capAt - CAP_LOGOUT_BEFORE_MS) {
          endSession('session_limit')
          return
        }
        if (now >= capAt - CAP_WARN_BEFORE_MS && !capWarnedRef.current) {
          capWarnedRef.current = true
          const minutes = Math.max(1, Math.round((capAt - now) / 60000))
          onCapWarningRef.current?.(minutes)
        }
      }
      const idleMs = now - lastActivityRef.current
      if (idleMs >= IDLE_LOGOUT_AFTER_MS) {
        endSession('inactivity')
      } else if (idleMs >= IDLE_WARN_AFTER_MS) {
        setIdleRemaining(Math.ceil((IDLE_LOGOUT_AFTER_MS - idleMs) / 1000))
        setIdleWarnOpen(true)
      }
    }, WATCHDOG_TICK_MS)
    return () => window.clearInterval(id)
  }, [isLoggedIn, endSession])

  // Live countdown while the idle warning is open.
  useEffect(() => {
    if (!idleWarnOpen) return
    const id = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current
      if (idleMs >= IDLE_LOGOUT_AFTER_MS) {
        endSession('inactivity')
        return
      }
      setIdleRemaining(
        Math.max(0, Math.ceil((IDLE_LOGOUT_AFTER_MS - idleMs) / 1000)),
      )
    }, 1000)
    return () => window.clearInterval(id)
  }, [idleWarnOpen, endSession])

  const handleStay = useCallback((): void => {
    const now = Date.now()
    lastActivityRef.current = now
    // A deliberate "Stay signed in" is real activity — bump the server idle clock too, not just
    // the client one, or the next refresh would still see stale activity.
    recordActivity(now)
    setIdleWarnOpen(false)
    runRefresh()
  }, [runRefresh, recordActivity])

  return { idleWarnOpen, idleRemaining, handleStay }
}
